import { Map, List } from 'immutable'
import {
  identity,
  isNil,
  isEmpty,
  prop,
  cond,
  propEq,
  T,
  always,
  compose,
  __,
} from 'ramda'
import { createSelector } from 'reselect'
import { createToJsSelector } from 'selectors/utils'
import {
  COLLECTIONS,
  FIRST_DROPDOWN,
  SECOND_DROPDOWN,
  THIRD_DROPDOWN,
  FOURTH_DROPDOWN,
  FIFTH_DROPDOWN,
  BASE_TYPE,
  MODULE_TYPE_FILTER,
  FEATURE,
  FAST_FEATURES,
} from 'utils/constants'
import {
  safeToJS,
  pick,
  getOr,
  getInOr,
  get,
  pickToArray,
  pickToJS,
} from 'utils/immutable'

const selectSearchByCollection = (state, collectionName) =>
  state.get('search').get(COLLECTIONS[collectionName])

const createSelectorForGivenKey = (key) => (state, collectionName) =>
  getInOr(Map(), ['search', COLLECTIONS[collectionName], key], state)

export const getSearchStringByCollection = createSelector(
  createSelectorForGivenKey('searchString'),
  (str) => str
)

export const getSearchByBusFilterByCollection = createSelector(
  createSelectorForGivenKey('busFilterFlag'),
  safeToJS
)

export const getDaysOutByCollection = createSelector(
  createSelectorForGivenKey('selectedScheduleDaysOut'),
  (str) => str
)

// first dropdown options list selector
const getFirstDropdownData = (state, props) =>
  getInOr(
    List(),
    ['search', props.collectionName, 'firstFilterCriteria'],
    state
  )
const selectFirstDropdownData = createSelector(getFirstDropdownData, identity)
const getDropdownDataList = createSelector(
  selectFirstDropdownData,
  (_, props) => props,
  (firstCriteriaList, { loading }) => {
    if (isEmpty(firstCriteriaList) || loading) return List()
    return firstCriteriaList
  }
)
export const selectFirstDropdownDataList =
  createToJsSelector(getDropdownDataList)

export const getFirstDropdownOptionsList = createSelector(
  getDropdownDataList,
  (firstCriteriaList) => {
    const formattedFilterList = firstCriteriaList.reduce(
      (acc, firstCriteria) => {
        const { id, label } = pickToJS(['id', 'label'], firstCriteria)
        const optionTerm = new Map({
          key: `${id}-${label}`,
          value: `${id}--${label}`,
          title: label,
        })
        return acc.push(optionTerm)
      },
      List()
    )

    return formattedFilterList
  }
)

export const selectFirstDropdownOptionsList = createToJsSelector(
  getFirstDropdownOptionsList
)

// get non multi filter first dropdown keys
const getNonMultiFilterKeys = createSelector(
  selectFirstDropdownData,
  (firstCriteriaList) => {
    if (isEmpty(firstCriteriaList)) return List()
    const nonMultiFilterIds = firstCriteriaList
      .filter((criteria) => criteria.get('id') !== BASE_TYPE)
      .map((criteria) => pick(['id'], criteria))
    return nonMultiFilterIds
  }
)
export const selectNonMultiFilterKeys = createToJsSelector(
  getNonMultiFilterKeys
)

// advanced dropdown options list selector by group key
const getAdvancedDropdownData = (state, props) =>
  getInOr(
    List(),
    ['search', props.collectionName, 'advancedFilterGroups'],
    state
  )
const selectAdvancedDropdownData = createSelector(
  getAdvancedDropdownData,
  identity
)

const getRPSecondDropdownOptions = (dropdownData) => {
  const { advancedFilterDataByGroup = List(), activeFilters } = dropdownData
  const getIsDisabled = compose(get(__, activeFilters), prop('value'))
  return advancedFilterDataByGroup.reduce((acc, optionData) => {
    const option = pickToJS(
      ['id', 'label', 'value', 'schemaId', 'group'],
      optionData
    )
    const { id, label, value, schemaId, group } = option
    const optionTerm = new Map({
      key: `${value}--${label}`,
      title: label,
      value: `${id}--${schemaId}--${value}--${group}`,
      disabled: getIsDisabled(option),
    })
    return acc.push(optionTerm)
  }, List())
}

const getRPThirdDropdownOptions = (dropdownData) => {
  const { advancedFilterDataByGroup = List() } = dropdownData
  if (propEq('collectionName', FEATURE, dropdownData)) {
    const activeFilters = prop('activeFilters', dropdownData)
    const getIsDisabled = compose(get(__, activeFilters), prop('value'))
    return advancedFilterDataByGroup.reduce((acc, optionData) => {
      const option = pickToJS(['id', 'label', 'value', 'group'], optionData)
      const { id, label, value, group } = option
      const optionTerm = new Map({
        key: `${value}--${label}`,
        title: label,
        value: `${id}--${value}--${label}--${group}`,
        disabled: getIsDisabled(option),
      })
      return acc.push(optionTerm)
    }, List())
  }
  return advancedFilterDataByGroup.reduce((acc, optionData) => {
    const option = pickToJS(
      ['id', 'label', 'value', 'schemaId', 'group'],
      optionData
    )
    const { id, label, value, schemaId, group } = option
    const optionTerm = new Map({
      key: `${value}--${label}`,
      title: label,
      value: `${id}--${schemaId}--${value}--${group}`,
    })
    return acc.push(optionTerm)
  }, List())
}

const getRPFourthDropdownOptions = (dropdownData) => {
  const { advancedFilterDataByGroup } = dropdownData
  return advancedFilterDataByGroup.reduce((acc, optionData) => {
    const option = pickToJS(['id', 'label', 'value', 'group'], optionData)
    const { id, label, value, group } = option
    const optionTerm = new Map({
      key: `${value}--${label}`,
      title: label,
      value: `${id}--${value}--${group}`,
    })
    return acc.push(optionTerm)
  }, List())
}

const getDisabled = (option, selection, activeFilters) => {
  const value = prop('value', option)
  const groupName = prop('groupName', selection)
  const schemaId = prop('schemaId', option)
  const key = `${value}--${groupName}--${schemaId}`
  return get(key, activeFilters)
}

const getRPFifthDropdownOptions = (dropdownData) => {
  const { advancedFilterDataByGroup, activeFilters, selection } = dropdownData
  return advancedFilterDataByGroup.reduce((acc, optionData) => {
    const option = pickToJS(
      ['id', 'label', 'value', 'schemaId', 'group'],
      optionData
    )
    const { id, label, value, schemaId, group } = option
    const optionTerm = new Map({
      key: `${value}--${label}`,
      title: label,
      value: `${id}--${label}--${schemaId}--${value}--${group}`,
      disabled: getDisabled(option, selection, activeFilters),
    })
    return acc.push(optionTerm)
  }, List())
}

const getDropdownKeyEq = propEq('dropdownKey')
const getOptionsByDropdown = cond([
  [getDropdownKeyEq(SECOND_DROPDOWN), getRPSecondDropdownOptions],
  [getDropdownKeyEq(THIRD_DROPDOWN), getRPThirdDropdownOptions],
  [getDropdownKeyEq(FOURTH_DROPDOWN), getRPFourthDropdownOptions],
  [getDropdownKeyEq(FIFTH_DROPDOWN), getRPFifthDropdownOptions],
  [T, always(null)],
])

const getActiveFilters = (state, props) =>
  getInOr(List(), ['search', props.collectionName, 'filters'], state)

const getAdvancedDropdownOptionsByGroup = createSelector(
  selectAdvancedDropdownData,
  getActiveFilters,
  (_, props) => props,
  (
    advancedFilterGroups,
    activeFilters,
    { dropdownKey, selection, collectionName }
  ) => {
    const getDropdownId = prop('id')
    if (isEmpty(advancedFilterGroups) || isNil(getDropdownId(selection))) {
      return List()
    }
    const id = getDropdownId(selection)
    const advancedFilterDataByGroup = get(id, advancedFilterGroups)
    const options = getOptionsByDropdown({
      dropdownKey,
      advancedFilterDataByGroup,
      activeFilters,
      selection,
      collectionName,
    })
    return options
  }
)

export const selectAdvancedDropdownOptionsByGroup = createToJsSelector(
  getAdvancedDropdownOptionsByGroup
)

const filterSelectionSelector = (state, { collectionName, filterKey }) =>
  state.getIn(['search', collectionName, 'filterSelections', filterKey])

export const getFilterSelectionByCollection = createSelector(
  filterSelectionSelector,
  safeToJS
)

// get current selection of advanced filters dropdowns by dropdownKey
const dropdownSelectionSelector = (state, { collectionName, dropdownKey }) =>
  getInOr(
    Map(),
    ['search', collectionName, 'dropdownSelections', dropdownKey],
    state
  )

export const getDropdownSelectionSelector = createSelector(
  dropdownSelectionSelector,
  safeToJS
)

const daysOutSelectionSelector = (state, { collectionName }) =>
  getInOr(Map(), ['search', collectionName, 'selectedScheduleDaysOut'], state)

export const getDaysOutSelectionSelector = createSelector(
  daysOutSelectionSelector,
  safeToJS
)

// get all current dropdown selections
const getAllDropdownSelectionsSelector = (
  state,
  { collectionName, dropdownKey }
) => getInOr(Map(), ['search', collectionName, 'dropdownSelections'], state)
const allDropdownSelectionsSelector = createSelector(
  getAllDropdownSelectionsSelector,
  identity
)

// const pickAndCastImm = curry((keyPairs, target, source) => keyPairs.reduce((accum, [sourceKey, targetKey]) => {
//   return accum.set(targetKey, source.get(sourceKey))
// }, target))

// convert data from dropdowns to filter term format
// For RPs --->  dropdowns 1 and 2 = filter1; dropdown 3 = filter 2; dropdown 3 and 4 = filter 3
const getMultiFilterMetaData = (filters, fifthVal, fifthSchemaId, thirdId) =>
  filters.reduce((acc, currData) => {
    const {
      value: currentValue,
      schemaId: currentSchemaId,
      group: currentGroup,
      label: currentLabel,
    } = pickToJS(['value', 'group', 'schemaId', 'label'], currData)
    const hasCommonFilter =
      currentValue === fifthVal &&
      currentSchemaId === fifthSchemaId &&
      currentGroup.includes(thirdId)
    if (hasCommonFilter) {
      const propName = currentGroup.split('_').pop()
      return acc.set(propName, currentLabel)
    }
    return acc
  }, Map())

const getSingleFilterMetaData = (filters, secondVal) =>
  filters.reduce((acc, currData) => {
    const {
      value: currentValue,
      group: currentGroup,
      label: currentLabel,
    } = pickToJS(['value', 'group', 'label'], currData)
    if (currentValue === secondVal) {
      const propName = currentGroup.split('_').pop()
      return acc.set(propName, currentLabel)
    }
    return acc
  }, Map())

const getRPFilterTermByDropdown = createSelector(
  selectAdvancedDropdownData,
  allDropdownSelectionsSelector,
  (_, props) => props,
  (advancedFilterGroups, dropdownSelections, { dropdownKey }) => {
    const dropdown1 = dropdownSelections.get(FIRST_DROPDOWN)
    const dropdown2 = dropdownSelections.get(SECOND_DROPDOWN)
    const dropdown3 = dropdownSelections.get(THIRD_DROPDOWN)
    const dropdown4 = dropdownSelections.get(FOURTH_DROPDOWN)
    const dropdown5 = dropdownSelections.get(FIFTH_DROPDOWN)
    const hasFirstFilterData =
      dropdownKey === SECOND_DROPDOWN && !isNil(dropdown2)
    const hasThirdFilterData =
      dropdownKey === FIFTH_DROPDOWN && !isNil(dropdown5)

    const { id: firstId, selectedValue: selectedValue1 } = pickToJS(
      ['id', 'selectedValue'],
      dropdown1
    )
    const {
      value,
      selectedValue: selectedValue2,
      schemaId: schema,
    } = pickToJS(['value', 'selectedValue', 'schemaId'], dropdown2)
    const filtergroups = advancedFilterGroups
      ? advancedFilterGroups.valueSeq().flatten(1)
      : List()
    const filterMetaData1 = getSingleFilterMetaData(filtergroups, value)
    const filter1 = Map({
      schema,
      value,
      selectedValue1,
      selectedValue2,
      filterType: firstId,
    })

    if (hasFirstFilterData) {
      return filter1.merge(filterMetaData1)
    }

    if (hasThirdFilterData) {
      const {
        value: thirdValue,
        schemaId: thirdSchema,
        selectedValue: selectedValue3,
        groupName: thirdGroupName,
        id: thirdId,
      } = pickToJS(
        ['value', 'schemaId', 'selectedValue', 'groupName', 'id'],
        dropdown3
      )
      const { selectedValue: selectedValue4, groupName: fourthGroupName } =
        pickToJS(['selectedValue', 'groupName'], dropdown4)
      const {
        value: fifthValue,
        schemaId: fifthSchema,
        selectedValue: selectedValue5,
        label: fifthLabel,
      } = pickToJS(['value', 'schemaId', 'selectedValue', 'label'], dropdown5)

      const filter2 = Map({
        schema: thirdSchema,
        value: thirdValue,
        selectedValue3,
      })
      const filter3 = Map({
        schema: fifthSchema,
        value: fifthValue,
        label: fifthLabel,
        thirdGroupName,
        fourthGroupName,
        selectedValue4,
        selectedValue5,
      })
      const filterMetaData3 = getMultiFilterMetaData(
        filtergroups,
        fifthValue,
        fifthSchema,
        thirdId
      )
      return Map({
        filter1: filter1.merge(filterMetaData1),
        filter2,
        filter3: filter3.merge(filterMetaData3),
        value: `${fifthValue}--${fourthGroupName}--${fifthSchema}`,
        filterType: dropdown1.get('id'),
      })
    }
    return Map()
  }
)

export const getRPFilterTermByDropdownSelector = createSelector(
  getRPFilterTermByDropdown,
  safeToJS
)

const getFeatureFilterTermByDropdown = createSelector(
  selectAdvancedDropdownData,
  allDropdownSelectionsSelector,
  (_, props) => props,
  (advancedFilterGroups, dropdownSelections, { dropdownKey }) => {
    const dropdown1 = dropdownSelections.get(FIRST_DROPDOWN)
    const dropdown2 = dropdownSelections.get(SECOND_DROPDOWN)
    const dropdown3 = dropdownSelections.get(THIRD_DROPDOWN)
    const dropdown4 = dropdownSelections.get(FOURTH_DROPDOWN)
    const dropdown5 = dropdownSelections.get(FIFTH_DROPDOWN)
    const hasOneFilterDataAndLevelTwo =
      dropdownKey === SECOND_DROPDOWN && !isNil(dropdown2)
    const hasOneFilterDataAndLevelThree =
      dropdownKey === THIRD_DROPDOWN && !isNil(dropdown3)
    const hasTwoFilterDataAndLevelThree =
      dropdownKey === FIFTH_DROPDOWN && !isNil(dropdown5)

    const { id: firstId, selectedValue: selectedValue1 } = pickToJS(
      ['id', 'selectedValue'],
      dropdown1
    )
    const {
      value,
      selectedValue: selectedValue2,
      schemaId: secondSchema,
    } = pickToJS(['value', 'selectedValue', 'schemaId'], dropdown2)
    const filtergroups = advancedFilterGroups
      ? advancedFilterGroups.valueSeq().flatten(1)
      : List()
    const filterMetaData1 = getSingleFilterMetaData(filtergroups, value)
    const filter1 = Map({
      schema: secondSchema,
      value,
      selectedValue1,
      selectedValue2,
      filterType: firstId,
    })
    if (hasOneFilterDataAndLevelTwo) {
      return filter1.merge(filterMetaData1)
    }
    if (hasOneFilterDataAndLevelThree) {
      const { value: thirdValue, selectedValue: selectedValue3 } = pickToJS(
        ['value', 'selectedValue'],
        dropdown3
      )
      const filter1WithLevel3 = Map({
        schema: secondSchema,
        value: thirdValue,
        selectedValue1,
        selectedValue2,
        selectedValue3,
        filterType: firstId,
        // value: `${thirdValue}--${secondGroupName}--${secondSchema}`
      })
      const filterMetaData2 = getSingleFilterMetaData(filtergroups, thirdValue)
      return filter1WithLevel3.merge(filterMetaData2)
    }

    if (hasTwoFilterDataAndLevelThree) {
      const {
        value: thirdValue,
        selectedValue: selectedValue3,
        groupName: thirdGroupName,
        id: thirdId,
      } = pickToJS(['value', 'selectedValue', 'groupName', 'id'], dropdown3)
      const { selectedValue: selectedValue4, groupName: fourthGroupName } =
        pickToJS(['selectedValue', 'groupName'], dropdown4)
      const {
        value: fifthValue,
        schemaId: fifthSchema,
        selectedValue: selectedValue5,
        label: fifthLabel,
      } = pickToJS(['value', 'schemaId', 'selectedValue', 'label'], dropdown5)
      const filter2 = Map({
        schema: fifthSchema,
        value: fifthValue,
        label: fifthLabel,
        thirdValue,
        thirdGroupName,
        fourthGroupName,
        selectedValue3,
        selectedValue4,
        selectedValue5,
      })
      const filterMetaData2 = getMultiFilterMetaData(
        filtergroups,
        fifthValue,
        fifthSchema,
        thirdId
      )
      return Map({
        filter1: filter1.merge(filterMetaData1),
        filter2: filter2.merge(filterMetaData2),
        value: `${fifthValue}--${fourthGroupName}--${fifthSchema}`,
        filterType: dropdown1.get('id'),
      })
    }
    return Map()
  }
)

export const selectFeatureFilterTermByDropdown = createSelector(
  getFeatureFilterTermByDropdown,
  safeToJS
)

const getVehicleFilterTermByDropdown = createSelector(
  allDropdownSelectionsSelector,
  (dropdownSelections) => {
    const dropdownList = dropdownSelections.toList()
    const filterTerms = dropdownList.map((x) =>
      new Map()
        .set('schemaId', x.get('schemaId'))
        .set('value', x.get('value'))
        .set('operator', 'in')
    )

    return filterTerms
  }
)

export const selectVehicleFilterTermByDropdown = createSelector(
  getVehicleFilterTermByDropdown,
  safeToJS
)

const selectSearch = (state) => state.get('search')

export const getFilterDataByCollection = createSelector(
  createSelectorForGivenKey('filterGroups'),
  safeToJS
)

export const getFiltersByCollection = createSelector(
  createSelectorForGivenKey('filters'),
  safeToJS
)

export const selectChipFeatureFilterTerm = createSelector(
  createSelectorForGivenKey('chipFilters'),
  safeToJS
)

const advancedFiltersForPostSelector = createSelector(
  (_, collectionName) => collectionName,
  createSelectorForGivenKey('filters'),
  (collectionName, filters) => {
    if (collectionName === FAST_FEATURES) {
      return filters
    }
    const filterDataList = filters.toList()
    const formattedFilterList = filterDataList.reduce((acc, filterTerm) => {
      const filterType = filterTerm.get('filterType')
      if (filterType === BASE_TYPE) {
        return acc.concat(
          pickToArray(['filter1', 'filter2', 'filter3'], filterTerm)
        )
      }
      if (filterType === MODULE_TYPE_FILTER) {
        return acc.concat(pickToArray(['filter1', 'filter2'], filterTerm))
      }
      return acc.push(filterTerm)
    }, List())
    const postFilterList = formattedFilterList.map((filterItem) =>
      new Map()
        .set('schemaId', filterItem.get('schema'))
        .set('value', filterItem.get('value'))
        .set('operator', 'in')
    )
    return postFilterList
  }
)

export const getFiltersByCollectionForPost = createSelector(
  advancedFiltersForPostSelector,
  safeToJS
)

const advancedFiltersForVehicleSelector = createSelector(
  createSelectorForGivenKey('filters'),
  (filters) => {
    const filterDataList = filters.toList()
    const postFilterList = filterDataList.map((filterItem) =>
      new Map()
        .set('schemaId', filterItem.get('schemaId'))
        .set('value', filterItem.get('value'))
        .set('operator', 'in')
    )
    return postFilterList
  }
)

export const getFiltersByCollectionName = createSelector(
  advancedFiltersForVehicleSelector,
  safeToJS
)

const searchResultsSelector = (state, collectionName) =>
  state.getIn(['search', collectionName, 'results'])

export const getSearchResults = createSelector(searchResultsSelector, safeToJS)

const isSearchingSelector = (state) => state.getIn(['search', 'isSearching'])

export const getIsSearching = createSelector(isSearchingSelector, identity)

const isSearchSubmittedSelector = (state) =>
  state.getIn(['search', 'searchSubmitted'])

export const getIsSearchSubmitted = createSelector(
  isSearchSubmittedSelector,
  identity
)

export const getSearchUri = createSelector(
  createSelectorForGivenKey('searchUri'),
  (searchUri) => searchUri
)

export const getAddToSearchRequestBody = createSelector(
  selectSearchByCollection,
  (collection) => getOr({}, 'addToSearchRequestBody', collection)
)

const selectSearchMetaByCollection = createSelector(
  selectSearchByCollection,
  (collection) =>
    pick(
      ['startIndex', 'totalResults', 'addToSearchRequestBody', 'searchUri'],
      collection
    )
)

export const getSearchMetaByCollection = createSelector(
  selectSearchMetaByCollection,
  safeToJS
)

export const getResultsLimit = createSelector(selectSearch, (search) =>
  search.get('limit')
)

const getEngineerViewAdvancedDropdownData = (state) =>
  getInOr(List(), ['search', 'vehicles', 'advancedFilterGroups'], state)

const getUpdatedDropdownOptions = (dropdownData) =>
  dropdownData.reduce((acc, optionData) => {
    const option = pickToJS(
      ['id', 'label', 'value', 'schemaId', 'group'],
      optionData
    )
    const { id, label, value, schemaId, group } = option
    const optionTerm = new Map({
      key: `${value}--${label}`,
      title: label,
      value: `${id}--${schemaId}--${value}--${group}`,
    })
    return acc.push(optionTerm)
  }, List())

const getFilterGroupList = (state) =>
  getInOr(List(), ['search', 'vehicles', 'firstFilterCriteria'], state)

const getEngineeringViewDropdownOptions = createSelector(
  getFilterGroupList,
  getEngineerViewAdvancedDropdownData,
  (_, props) => props,
  (filterGroupList, advancedFilterGroups, { loading }) => {
    if (isEmpty(filterGroupList) || loading) return Map()
    return filterGroupList.reduce((acc, currData) => {
      const keyName = currData.get('value')
      const collection = getOr(List(), keyName, advancedFilterGroups)
      const updatedOptions = getUpdatedDropdownOptions(collection)
      return acc.set(keyName, updatedOptions)
    }, Map())
  }
)

export const getEngineerViewDropdownSelector = createSelector(
  getEngineeringViewDropdownOptions,
  safeToJS
)

const getEngineerViewSelectedDaysOut = (state) =>
  getInOr('', ['search', 'vehicles', 'selectedScheduleDaysOut'], state)

export const getEngineerViewSelectedDaysOutSelector = createSelector(
  getEngineerViewSelectedDaysOut,
  (str) => str
)
